import { h } from 'preact'
import clsx from 'clsx'

interface PromolineData {
  title: string
  body?: string
  cta_text?: string
  cta_url?: string
  text_colour: string
  background_colour?: string
  priority: number
  end_date?: string
  countdown?: string[]
}

interface PromolineProps {
  promoline: PromolineData
  promolineLocation?: string
  promolineSize?: 'small' | 'medium' | 'large'
  title?: string
  body?: string
  cta_text?: string
  cta_url?: string
  text_colour?: string
  bg_colour?: string
  end_date?: string
  countdown?: string[]
}

export const Promoline = ({
  promoline,
  promolineLocation,
  promolineSize,
  title: titleOverride,
  body: bodyOverride,
  cta_text: ctaTextOverride,
  cta_url: ctaUrlOverride,
  text_colour: textColourOverride,
  bg_colour: bgColourOverride,
  end_date: endDateOverride,
  countdown: countdownOverride,
  className
}: PromolineProps & { className?: string }) => {
  if (!promoline || !promoline.title) return null

  const title = titleOverride ?? promoline.title
  const body = bodyOverride ?? promoline.body
  const ctaText = ctaTextOverride ?? promoline.cta?.text
  const ctaUrl = ctaUrlOverride ?? promoline.cta?.url
  const textColour = textColourOverride ?? promoline.text_colour
  const bgColour = bgColourOverride ?? promoline.background_colour
  const endDate = endDateOverride ?? promoline.end_date
  const countdown = countdownOverride ?? promoline.countdown

  const countdownString = Array.isArray(countdown) ? countdown.join(',') : ''

  const shouldShowCountdown = (() => {
    if (!endDate || !countdownString || !promolineLocation) return false
    return countdownString.includes(promolineLocation)
  })()

  const getSizeConfig = () => {
    switch (promolineSize) {
      case 'small':
        return {
          container: 'w-fit',
          containerBg: '',
          titleClass: 'paragraph-xs font-medium',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
      case 'medium':
        return {
          container: 'w-fit bg-[--bg-colour]',
          containerBg: 'px-0.5 py-0.188',
          titleClass: 'paragraph-xs font-medium',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
      case 'large':
        return {
          container: 'w-full bg-[--bg-colour]',
          containerBg: 'p-1',
          titleClass: 'paragraph-s font-medium',
          countdownLayout: 'medium',
          countdownPosition: 'block'
        }
    }
  }

  const sizeConfig = getSizeConfig()
  const Component = ctaUrl ? 'a' : 'div'

  const componentProps = ctaUrl ? {
    href: ctaUrl,
    'aria-label': title
  } : {}

  const CountdownTimer = ({
    endDate,
    layout = 'small'
  }: {
    endDate: string
    layout?: 'small' | 'medium' | 'large'
  }) => {
    if (!endDate) return null

    const getLayoutConfig = () => {
      switch (layout) {
        case 'small':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-0.125',
            itemClass: 'flex',
            numbersClass: 'paragraph-xs font-medium',
            textClass: 'paragraph-xs font-medium'
          }
        case 'medium':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-1 children:shrink-0',
            itemClass: 'flex flex-col gap-y-0.25 items-center justify-center text-center',
            numbersClass: 'h4 font-heading font-semibold leading-none',
            textClass: 'text-xxs font-semibold uppercase'
          }
        case 'large':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-1.25 children:shrink-0',
            itemClass: 'flex flex-col gap-y-0.5 items-center justify-center text-center',
            numbersClass: 'h2 font-heading font-semibold leading-none',
            textClass: 'text-s font-semibold uppercase'
          }
      }
    }

    const config = getLayoutConfig()
    const labels = {
      small: ['d,', 'h,', 'm,', 's'],
      medium: ['Days', 'Hrs', 'Mins', 'Secs'],
      large: ['Days', 'Hrs', 'Mins', 'Secs']
    }
    const labelArray = labels[layout]

    return h(
      'x-countdown',
      {
        'data-end-date': new Date(endDate).toISOString(),
        'data-ui': 'visible',
        className: 'group/countdown shrink-0'
      },
      h('div', { className: config.wrapperClass },
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-days': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[0])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-hours': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[1])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-minutes': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[2])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-seconds': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[3])
        )
      )
    )
  }

  return (
    <Component
      {...componentProps}
      className={clsx(
        'shrink-0 flex',
        !shouldShowCountdown && 'flex-wrap',
        'items-center justify-between gap-x-0.5 gap-y-0.25',
        sizeConfig.container,
        'h-fit text-[--text-colour]',
        bgColour && sizeConfig.containerBg,
        ctaUrl && 'link-fade',
        className
      )}
      style={{
        '--text-colour': textColour,
        '--bg-colour': bgColour || 'transparent'
      } as React.CSSProperties}
    >
      <div className={clsx(
        'grow flex gap-x-0.5 gap-y-0.25',
        sizeConfig.container,
        shouldShowCountdown && sizeConfig.countdownPosition === 'block' ? 'flex-col' : 'items-center'
      )}>
        {sizeConfig.countdownPosition === 'inline' ? (
          <div className={clsx(
            'flex items-center',
            sizeConfig.countdownPosition === 'block' ? 'shrink-0' : 'flex-wrap'
          )}>
            <span className={sizeConfig.titleClass}>
              {title}{shouldShowCountdown ? '\u00A0|\u00A0' : ''}
            </span>
            {shouldShowCountdown && (
              <CountdownTimer
                endDate={endDate}
                layout={sizeConfig.countdownLayout as 'small' | 'medium' | 'large'}
              />
            )}
          </div>
        ) : (
          <>
            {(title || body) && (
              <div className="grow flex flex-col gap-y-0.25">
                {title && (
                  <span className={sizeConfig.titleClass}>
                    {title}
                  </span>
                )}
                {body && (
                  <span className="grow paragraph-xs link-fade">
                    {body}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {ctaText && promolineSize === 'large' && (
          <span className="shrink-0 size-fit font-line text-xs">
            {ctaText}
          </span>
        )}
      </div>

      {shouldShowCountdown && sizeConfig.countdownPosition === 'block' && (
        <CountdownTimer
          endDate={endDate}
          layout={sizeConfig.countdownLayout as 'small' | 'medium' | 'large'}
        />
      )}
    </Component>
  )
}
